import { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Edit, User, Mail, Star, TrendingUp, CheckCircle, Camera } from 'lucide-react';

import { useProfile } from '../hooks/useProfile';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import type { Profile } from '../types';

const avatarOptions = [
    'bottts', 'adventurer', 'big-ears', 'avataaars', 'initials', 'micah'
];

const ProfileForm = ({ profile, onSave, onCancel, isSaving }: { profile: Profile, onSave: (updates: Partial<Profile>) => void, onCancel: () => void, isSaving: boolean }) => {
    const [formData, setFormData] = useState({
        display_name: profile.display_name || '',
        username: profile.username || '',
        avatar_url: profile.avatar_url || `https://api.dicebear.com/6.x/bottts/svg?seed=${profile.id}`
    });
    const [selectedAvatarType, setSelectedAvatarType] = useState(formData.avatar_url.split('/')[4] || 'bottts');

    const handleAvatarChange = (type: string) => {
        setSelectedAvatarType(type);
        setFormData({ ...formData, avatar_url: `https://api.dicebear.com/6.x/${type}/svg?seed=${profile.id}` });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, type: 'spring' }}>
            <form onSubmit={handleSubmit} className="space-y-6 p-8 bg-white rounded-3xl shadow-2xl">
                <h2 className="text-4xl font-bold text-center text-purple-700">Edit Your Profile</h2>

                <div className="flex flex-col items-center space-y-4">
                    <div className="relative group">
                        <img src={formData.avatar_url} alt="Avatar Preview" className="w-32 h-32 rounded-full border-4 border-purple-300" />
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Camera className="w-10 h-10 text-white" />
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3">
                        {avatarOptions.map(type => (
                            <button key={type} type="button" onClick={() => handleAvatarChange(type)} className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-all ${selectedAvatarType === type ? 'border-purple-500 scale-110' : 'border-transparent'}`}>
                                <img src={`https://api.dicebear.com/6.x/${type}/svg?seed=${profile.id}`} alt={`${type} avatar option`} />
                            </button>
                        ))}
                    </div>
                </div>

                <div className="relative">
                    <User className="absolute top-1/2 -translate-y-1/2 left-4 w-6 h-6 text-gray-400" />
                    <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                        placeholder="Your Name"
                    />
                </div>
                <div className="relative">
                    <Mail className="absolute top-1/2 -translate-y-1/2 left-4 w-6 h-6 text-gray-400" />
                    <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                        placeholder="Your Username"
                    />
                </div>
                <div className="flex justify-center space-x-4 pt-4">
                    <Button type="submit" loading={isSaving} className="rounded-full px-8 py-3 text-lg font-semibold bg-purple-600 hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                        Save Changes
                    </Button>
                    <Button type="button" variant="outline" onClick={onCancel} className="rounded-full px-8 py-3 text-lg font-semibold">
                        Cancel
                    </Button>
                </div>
            </form>
        </motion.div>
    );
};

const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode, label: string, value: string | number, color: string }) => (
    <motion.div whileHover={{ scale: 1.05 }} className={`py-6 px-4 bg-gradient-to-br ${color} rounded-2xl shadow-lg text-white`}>
        <div className="flex items-center justify-center mb-2">{icon}</div>
        <p className="text-3xl font-bold text-center">{value}</p>
        <p className="text-sm text-center opacity-90">{label}</p>
    </motion.div>
);

const AchievementCard = ({ achievement }: { achievement: any }) => (
    <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow flex items-center space-x-6 transform hover:-translate-y-1"
    >
        <div className="p-4 bg-yellow-100 rounded-full">
            <Award className="w-10 h-10 text-yellow-500" />
        </div>
        <div>
            <h3 className="font-bold text-xl text-gray-800">{achievement.achievements.name}</h3>
            <p className="text-gray-600">{achievement.achievements.description}</p>
            <p className="text-sm text-gray-400 mt-1">Earned on: {new Date(achievement.earned_at).toLocaleDateString()}</p>
        </div>
    </motion.div>
);

export default function ProfilePage() {
    const { profile, achievements, isLoadingProfile, isUpdatingProfile, updateProfile } = useProfile();
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = async (updates: Partial<Profile>) => {
        await updateProfile(updates);
        setIsEditing(false);
    };

    if (isLoadingProfile || !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-50">
                <div className="text-center">
                    <LoadingSpinner size="lg" className="mb-4" />
                    <p className="text-lg text-blue-700 font-semibold">Loading Your Profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-100 to-purple-100 font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-5xl mx-auto">
                {isEditing ? (
                    <ProfileForm profile={profile} onSave={handleSave} onCancel={() => setIsEditing(false)} isSaving={isUpdatingProfile} />
                ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
                        <header className="relative text-center mb-12 bg-white p-8 rounded-3xl shadow-xl bg-opacity-70 backdrop-blur-lg">
                            <div className="absolute top-4 right-4">
                                <Button onClick={() => setIsEditing(true)} variant="ghost" className="rounded-full p-3 hover:bg-purple-100 transition-colors">
                                    <Edit className="w-6 h-6 text-purple-600" />
                                </Button>
                            </div>
                            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 150 }}>
                                <img
                                    src={profile.avatar_url || `https://api.dicebear.com/6.x/bottts/svg?seed=${profile.id}`}
                                    alt="Profile"
                                    className="w-32 h-32 rounded-full mx-auto mb-4 border-8 border-white shadow-lg"
                                />
                            </motion.div>
                            <h1 className="text-5xl font-extrabold text-gray-800 tracking-tight">{profile.display_name}</h1>
                            <p className="text-xl text-gray-500">@{profile.username}</p>

                            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <StatCard icon={<Star className="w-8 h-8" />} label="Points" value={profile.total_score ?? 0} color="from-yellow-400 to-orange-500" />
                                <StatCard icon={<CheckCircle className="w-8 h-8" />} label="Words Mastered" value={profile.words_mastered ?? 0} color="from-green-400 to-emerald-500" />
                                <StatCard icon={<TrendingUp className="w-8 h-8" />} label="Streak" value={profile.streak_days ?? 0} color="from-red-400 to-pink-500" />
                                <StatCard icon={<Award className="w-8 h-8" />} label="Level" value={profile.level ?? 1} color="from-blue-400 to-indigo-500" />
                            </div>
                        </header>

                        <main>
                            <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">Your Achievements</h2>
                            {achievements && achievements.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {achievements.map((ach) => (
                                        <AchievementCard key={ach.id} achievement={ach} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-16 bg-white/50 rounded-2xl shadow-lg">
                                    <Award className="mx-auto h-24 w-24 text-gray-400 mb-6" />
                                    <h3 className="text-2xl font-bold text-gray-800">No Achievements Yet!</h3>
                                    <p className="text-gray-600 mt-2">Keep practicing to earn cool badges.</p>
                                </div>
                            )}
                        </main>
                    </motion.div>
                )}
            </div>
        </div>
    );
}